import { showToast } from "services/utils";
import React from 'react';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';

/*
       use 

       const helperId = {
           id: 754,
           uuid: 4521,
           name: 'dfv'
       };

       const template = "mam/:id/:uuid?name";
       const finalUrl = generateURL(template, helperId);
*/


export const generateURL = (template, params) => {
    if (!!params === false) {
        showToast("Url object is not defined", false)
        return
    }
    let url = template;
    url = url.replace(/:([a-zA-Z0-9_]+)/g, (match, key) => {
        return encodeURIComponent(params[key] ?? match);
    });
    url = url.replace(/([?&])([a-zA-Z0-9_]+)/g, (match, sep, key) => {
        if (params[key] !== undefined) {
            return `${sep}${key}=${encodeURIComponent(params[key])}`;
        }
        return match;
    });
    return url;
}

export const getDuration = (start, end) => {
    const parseInput = (input) => {
        if (typeof input === 'string') {
            return input.includes('T')
                ? new Date(input)
                : new Date(`1970-01-01T${input}:00Z`);
        } else if (typeof input === 'object' && input.date && input.time) {
            return new Date(`${input.date.split('T')[0]}T${input.time}:00Z`);
        }
        return null;
    };

    const startDate = parseInput(start);
    const endDate = parseInput(end);

    if (!startDate || !endDate || isNaN(startDate) || isNaN(endDate)) {
        return '-';
    }

    const isNegative = endDate < startDate;
    const [from, to] = isNegative ? [endDate, startDate] : [startDate, endDate];

    let years = to.getFullYear() - from.getFullYear();
    let months = to.getMonth() - from.getMonth();
    let days = to.getDate() - from.getDate();
    let hours = to.getHours() - from.getHours();
    let minutes = to.getMinutes() - from.getMinutes();

    if (minutes < 0) {
        minutes += 60;
        hours--;
    }

    if (hours < 0) {
        hours += 24;
        days--;
    }

    if (days < 0) {
        const previousMonth = new Date(to.getFullYear(), to.getMonth(), 0);
        days += previousMonth.getDate();
        months--;
    }

    if (months < 0) {
        months += 12;
        years--;
    }

    const parts = [];
    if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
    if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`);

    return parts.length ? parts.join(', ') : '0 min';
}

export const insertDynamicColumns = (columns, optionMap, validTypes = ['dropdown', 'dynamicDropdown']) => {
    const updatedColumns = columns.map(column => {
        if (
            validTypes.includes(column.type) &&
            optionMap?.[column.uniqueName]
        ) {
            return {
                ...column,
                options: optionMap[column.uniqueName],
            };
        }
        return column;
    });
    return columns
}

// Helper function for deep equality check
const isEqual = (a, b) => {
    if (a === b) return true;
    // Check if both are objects
    if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        if (keysA.length !== keysB.length) return false
        return keysA.every(key => isEqual(a[key], b[key]));
    }
    return false;
}

export const removeMatchingItems = (inputArray, itemToRemove) => {
    return inputArray?.filter(item => !isEqual(item, itemToRemove));
}

export const isISODateString = (str) => {
    if (typeof str !== "string") return false;
    const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
    return isoRegex.test(str);
};


export const hasNonEmptyObject = (arr = []) => {
    if (!Array.isArray(arr)) return false; // safety check

    return arr.some(obj =>
        obj && typeof obj === "object" && Object.keys(obj).length > 0
    );
}

export const getRefValueByPath = (ref, path = "") => {
    if (!ref?.current) return undefined;

    const keys = path.split(".");
    let current = ref.current;

    for (let key of keys) {
        if (current == null || typeof current !== "object") return undefined;
        current = current[key];
    }

    return current;
};

export const setRefValueByPath = (ref, path = "", value) => {
    if (!ref?.current) return;

    const keys = path.split(".");
    let current = ref.current;

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];

        // If it's the last key, set the value
        if (i === keys.length - 1) {
            current[key] = value;
            return;
        }

        // If not exist, create an object
        if (!current[key] || typeof current[key] !== "object") {
            current[key] = {};
        }

        current = current[key];
    }
};


export const toCamelCase = (str = "") => {
    return str
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());
}